/**
 * Dosing Page - View dosing events, volume analysis, and manage dosing schedules
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Title,
  Stack,
  Paper,
  Alert,
  Loader,
  Center,
  Group,
  Button,
  ActionIcon,
  Tooltip,
  Affix,
  Transition,
  Tabs,
  Badge,
} from '@mantine/core';
import { DatePickerInput, type DatesRangeValue } from '@mantine/dates';
import {
  IconAlertCircle,
  IconFlask,
  IconMessagePlus,
  IconClock,
  IconCalendarRepeat,
  IconPlus,
} from '@tabler/icons-react';
import { ChatSidebar } from '@/components/ChatSidebar';
import { useTank } from '@/contexts/TankContext';
import {
  useDosingEvents,
  useDosingSchedules,
  useCreateDosingSchedule,
  useUpdateDosingSchedule,
  useDeleteDosingSchedule,
} from '@/hooks/useDosingEvents';
import { Last24HMetrics } from '@/features/dosetronic/Last24HMetrics';
import { DosetronicCharts } from '@/features/dosetronic/DosetronicCharts';
import { DosetronicEventsTable } from '@/features/dosetronic/DosetronicEventsTable';
import { DosingSchedulesList } from '@/features/dosing/DosingSchedulesList';
import { DosingScheduleModal } from '@/features/dosing/DosingScheduleModal';
import type { DosingEventFilters } from '@/api/dosing';
import type { DosingSchedule } from '@/types/dosing';

export default function DosingPage() {
  const [chatOpen, setChatOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('events');
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<DosingSchedule | null>(null);
  const { selectedTank } = useTank();

  // Default to last 30 days
  const defaultEndDate = new Date();
  const defaultStartDate = new Date();
  defaultStartDate.setDate(defaultStartDate.getDate() - 30);

  const [dateRange, setDateRange] = useState<DatesRangeValue>([
    defaultStartDate,
    defaultEndDate,
  ]);

  // Compute filters from date range (auto-apply)
  // Note: Mantine DatePickerInput may return Date objects OR ISO strings
  // Only apply filters when BOTH dates are selected to avoid mid-selection API calls
  const filters: DosingEventFilters = {};
  const hasStartDate = dateRange[0] !== null && dateRange[0] !== undefined;
  const hasEndDate = dateRange[1] !== null && dateRange[1] !== undefined;

  if (hasStartDate && hasEndDate) {
    // Extract start date
    if (dateRange[0] instanceof Date) {
      filters.start_date = dateRange[0].toISOString().split('T')[0];
    } else if (typeof dateRange[0] === 'string') {
      filters.start_date = dateRange[0].split('T')[0];
    }
    // Extract end date
    if (dateRange[1] instanceof Date) {
      filters.end_date = dateRange[1].toISOString().split('T')[0];
    } else if (typeof dateRange[1] === 'string') {
      filters.end_date = dateRange[1].split('T')[0];
    }
  }

  // Only fetch when both dates are selected
  const isRangeComplete = !!filters.start_date && !!filters.end_date;

  // Fetch dosing events with React Query (only when range is complete)
  const { data: response, isLoading, error } = useDosingEvents(filters, {
    enabled: isRangeComplete,
  });

  const { data: schedulesData } = useDosingSchedules();
  const createSchedule = useCreateDosingSchedule();
  const updateSchedule = useUpdateDosingSchedule();
  const deleteSchedule = useDeleteDosingSchedule();
  const activeScheduleCount = schedulesData?.schedules?.filter(s => s.is_active).length ?? 0;

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header with action buttons */}
        <Group justify="space-between" wrap="wrap">
          <Title order={1}>Dosing</Title>
          <Group gap="sm" wrap="wrap">
            <Button
              variant="filled"
              leftSection={<IconCalendarRepeat size={16} />}
              onClick={() => {
                setEditingSchedule(null);
                setScheduleModalOpen(true);
              }}
            >
              Create Dosing Schedule
            </Button>
            <Button
              variant="filled"
              color="green"
              leftSection={<IconPlus size={16} />}
              onClick={() => setChatOpen(true)}
            >
              Log One-Off Dose
            </Button>
            <Button
              component={Link}
              to="/trace-dosing"
              variant="outline"
              leftSection={<IconFlask size={16} />}
            >
              Trace Dosing Batches
            </Button>
          </Group>
        </Group>

        {/* Tabs */}
        <Tabs value={activeTab} onChange={(v) => setActiveTab(v ?? 'events')}>
          <Tabs.List>
            <Tabs.Tab value="events" leftSection={<IconClock size={14} />}>
              Events
            </Tabs.Tab>
            <Tabs.Tab
              value="schedules"
              leftSection={<IconCalendarRepeat size={14} />}
              rightSection={
                activeScheduleCount > 0 ? (
                  <Badge size="sm" variant="filled" circle>
                    {activeScheduleCount}
                  </Badge>
                ) : undefined
              }
            >
              Schedules
            </Tabs.Tab>
          </Tabs.List>

          {/* Events Tab — existing content */}
          <Tabs.Panel value="events" pt="md">
            <Stack gap="lg">
              {/* Error State */}
              {error && (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  title="Error loading dosing events"
                  color="red"
                >
                  {error instanceof Error ? error.message : 'An unexpected error occurred'}
                </Alert>
              )}

              {/* Date Range Controls */}
              <Paper p="md" withBorder>
                <DatePickerInput
                  type="range"
                  label="Date Range"
                  placeholder="Pick dates range"
                  defaultValue={dateRange}
                  onChange={(dates: DatesRangeValue) => {
                    // Handle partial selection (user clicked first date but not second)
                    if (!dates) return;

                    if (Array.isArray(dates)) {
                      const [start, end] = dates;

                      // Only update state if both dates are selected
                      if (start && end) {
                        const startDate = start instanceof Date ? start : new Date(start);
                        const endDate = end instanceof Date ? end : new Date(end);
                        setDateRange([startDate, endDate]);
                      }
                    }
                  }}
                  maxDate={new Date()}
                  clearable={false}
                  valueFormat="MMM DD, YYYY"
                  allowSingleDateInRange
                  style={{ width: 300 }}
                />
              </Paper>

              {/* Loading State */}
              {isLoading && (
                <Center py="xl">
                  <Loader size="lg" />
                </Center>
              )}

              {/* Data Sections */}
              {!isLoading && response && (
                <>
                  {/* Last 24H Metrics */}
                  <Last24HMetrics events={response.data} />

                  {/* Daily Volume Chart */}
                  <DosetronicCharts events={response.data} />

                  {/* Recent Events Table */}
                  <DosetronicEventsTable events={response.data} />
                </>
              )}
            </Stack>
          </Tabs.Panel>

          {/* Schedules Tab */}
          <Tabs.Panel value="schedules" pt="md">
            <DosingSchedulesList
              schedules={schedulesData?.schedules ?? []}
              onEdit={(s) => {
                setEditingSchedule(s);
                setScheduleModalOpen(true);
              }}
              onDelete={(s) => {
                if (window.confirm(`Delete schedule for ${s.chemical_name}? Generated events will be kept.`)) {
                  deleteSchedule.mutate({ scheduleId: s.id, deleteEvents: false });
                }
              }}
              onToggleActive={(s) => {
                updateSchedule.mutate({
                  scheduleId: s.id,
                  data: { is_active: !s.is_active },
                });
              }}
              onCreateNew={() => {
                setEditingSchedule(null);
                setScheduleModalOpen(true);
              }}
            />
          </Tabs.Panel>
        </Tabs>
      </Stack>

      {/* FAB for quick chat entry */}
      <Affix position={{ bottom: 20, right: 20 }}>
        <Transition transition="slide-up" mounted={!chatOpen}>
          {(styles) => (
            <Tooltip label="Log manual dosing" position="left" withArrow>
              <ActionIcon
                style={styles}
                size={56}
                radius="xl"
                variant="filled"
                color="blue"
                onClick={() => setChatOpen(true)}
                aria-label="Log manual dosing via chat"
              >
                <IconMessagePlus style={{ width: 28, height: 28 }} stroke={2} />
              </ActionIcon>
            </Tooltip>
          )}
        </Transition>
      </Affix>

      {/* Chat Sidebar for quick dosing entry */}
      {selectedTank && (
        <ChatSidebar
          opened={chatOpen}
          onClose={() => setChatOpen(false)}
          tankId={selectedTank.id}
          tankName={selectedTank.name}
        />
      )}

      <DosingScheduleModal
        opened={scheduleModalOpen}
        onClose={() => {
          setScheduleModalOpen(false);
          setEditingSchedule(null);
        }}
        onSubmit={(data) => {
          if (editingSchedule) {
            updateSchedule.mutate(
              { scheduleId: editingSchedule.id, data },
              { onSuccess: () => { setScheduleModalOpen(false); setEditingSchedule(null); } },
            );
          } else {
            createSchedule.mutate(data, {
              onSuccess: () => { setScheduleModalOpen(false); },
            });
          }
        }}
        schedule={editingSchedule ?? undefined}
        isLoading={createSchedule.isPending || updateSchedule.isPending}
      />
    </Container>
  );
}
